import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils/cn';
import { routePaths } from '@/app/routes';
import { useActivePatient } from '../hooks/useActivePatient';
import type { Patient } from '../types/patient.types';

function initials(p: Patient): string {
  return `${p.firstName[0] ?? ''}${p.lastName[0] ?? ''}`.toUpperCase() || '?';
}

function Avatar({ patient, className }: { patient: Patient; className?: string }) {
  return (
    <span
      className={cn(
        'grid place-items-center rounded-full bg-primary-light text-sm font-bold text-primary',
        className,
      )}
    >
      {initials(patient)}
    </span>
  );
}

interface PatientSwitcherProps {
  /** 'plain' neutral pill · 'account' orange header button · 'sidebar' full-width on the navy sidebar. */
  variant?: 'plain' | 'account' | 'sidebar';
}

/** Control to view and switch the active patient (account holder / dependents). */
export function PatientSwitcher({ variant = 'plain' }: PatientSwitcherProps) {
  const { t } = useTranslation();
  const { patient, patients, isLoading, setActivePatient } = useActivePatient();
  const [open, setOpen] = useState(false);

  const account = variant === 'account';
  const sidebar = variant === 'sidebar';
  const dark = account || sidebar; // white-text variants

  if (isLoading) {
    return <div className="h-10 w-40 animate-pulse rounded-input bg-muted-200" />;
  }

  // No patient on this account yet (fresh registration — or the list failed
  // to load): a static placeholder, not the loading shimmer, which would
  // otherwise pulse forever with nothing to wait for.
  if (!patient) {
    return (
      <div
        className={cn(
          'flex items-center gap-2.5 rounded-input',
          account && 'bg-accent py-2 pl-3 pr-3.5 text-white',
          sidebar && 'w-full bg-white/10 py-2 pl-2 pr-3 text-white',
          !dark && 'bg-muted-100 py-1.5 pl-1.5 pr-3',
        )}
      >
        <span
          className={cn(
            'grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-bold',
            dark ? 'bg-white/10 text-white/70' : 'bg-muted-200 text-ink-mute',
          )}
        >
          ?
        </span>
        <span className="min-w-0 flex-1">
          <span className={cn('block truncate text-sm font-semibold', dark ? 'text-white' : 'text-ink')}>
            {t('patients.none')}
          </span>
          <span className={cn('block text-xs', dark ? 'text-white/60' : 'text-ink-mute')}>
            {t('patients.none_hint')}
          </span>
        </span>
      </div>
    );
  }

  return (
    <div className={cn('relative', sidebar && 'w-full')}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-2.5 rounded-input text-left',
          account && 'bg-accent py-2 pl-3 pr-3.5 text-white hover:brightness-95',
          sidebar && 'w-full bg-white/10 py-2 pl-2 pr-3 text-white hover:bg-white/15',
          !dark && 'bg-muted-100 py-1.5 pl-1.5 pr-3 hover:bg-muted-200',
        )}
      >
        {account ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <Avatar patient={patient} className="h-8 w-8" />
        )}
        <span className={cn('min-w-0', sidebar && 'flex-1')}>
          <span className={cn('block truncate text-sm font-semibold', dark ? 'text-white' : 'text-ink')}>
            {account ? patient.fullName.split(' ')[0] || t('nav.account') : patient.fullName}
          </span>
          {!account && (
            <span className={cn('block text-xs', sidebar ? 'text-white/60' : 'text-ink-mute')}>
              {patient.isPrimary ? t('patients.you') : t('patients.dependent')}
            </span>
          )}
        </span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={dark ? 'text-white/80' : 'text-ink-mute'}>
          <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className={cn('absolute z-20 mt-2 overflow-hidden rounded-card bg-surface py-1 shadow-card', sidebar ? 'left-0 right-0' : 'right-0 w-64')}>
            {patients.length > 1 && (
              <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-mute">
                {t('patients.switch')}
              </p>
            )}
            {patients.map((p) => (
              <button
                key={p.fhirId}
                type="button"
                onClick={() => {
                  setActivePatient(p.fhirId);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted-100',
                  p.fhirId === patient.fhirId && 'bg-primary-light/40',
                )}
              >
                <Avatar patient={p} className="h-8 w-8" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-ink">{p.fullName}</span>
                  <span className="block text-xs text-ink-mute">
                    {p.isPrimary ? t('patients.you') : t('patients.dependent')}
                  </span>
                </span>
              </button>
            ))}

            <div className="my-1 border-t border-muted-200" />
            <Link
              to={routePaths.patients}
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-primary hover:bg-muted-100"
            >
              <span className="grid h-8 w-8 place-items-center rounded-full border border-dashed border-primary/40">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              {t('patients.list.cta')}
            </Link>
            <Link
              to={routePaths.addPatient}
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-primary hover:bg-muted-100"
            >
              <span className="grid h-8 w-8 place-items-center rounded-full border border-dashed border-primary/40 text-lg leading-none">
                +
              </span>
              {t('patients.add.cta')}
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
